import React, { useState } from 'react';
import Header from '../components/Header';
import { Trash2, AlertTriangle, Shield, CheckCircle, Info, Clock, Database } from 'lucide-react';
import { Seo } from '../src/seo/Seo';

const DataDelete: React.FC = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletionConfirmed, setDeletionConfirmed] = useState(false);
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');

  // Fonctions par défaut pour les props du Header
  const handleDownloadClick = () => {
    // Rediriger vers la page d'accueil pour le téléchargement
    window.location.href = '/';
  };

  const handleMySpaceClick = () => {
    // Rediriger vers la page d'accueil pour My Space
    window.location.href = '/';
  };

  const handleDeleteRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !reason) return;
    
    setIsDeleting(true);
    
    // Simulation d'un processus de suppression
    setTimeout(() => {
      setIsDeleting(false);
      setDeletionConfirmed(true);
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50">
      <Seo
        title={{ fr: 'Suppression des données — Burok', en: 'Data deletion — Burok' }}
        description={{
          fr: 'Demandez la suppression de vos données et découvrez les étapes et délais de traitement chez Burok.',
          en: 'Request deletion of your data and learn about the process and expected timelines at Burok.',
        }}
        canonicalPath={{ fr: '/data/delete', en: '/en/data/delete' }}
      />
      <Header onDownloadClick={handleDownloadClick} onMySpaceClick={handleMySpaceClick} />
      
      {/* Hero Section */}
      <section className="px-4 pt-20 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex justify-center items-center mb-6 w-16 h-16 bg-red-100 rounded-full">
            <Trash2 className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="mb-6 text-4xl font-bold text-gray-900 md:text-5xl">
            Suppression de vos Données
          </h1>
          <p className="mx-auto max-w-2xl text-xl text-gray-600">
            Exercice de votre droit à l'effacement (droit à l'oubli) - RGPD
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          
          {/* Warning Alert */}
          <div className="p-6 mb-8 bg-red-50 rounded-2xl border border-red-200">
            <div className="flex items-start">
              <AlertTriangle className="flex-shrink-0 mt-1 mr-3 w-6 h-6 text-red-600" />
              <div>
                <h3 className="mb-2 font-semibold text-red-900">Attention : Action Irréversible</h3>
                <p className="text-red-800">
                  La suppression de vos données est définitive et ne peut pas être annulée. 
                  Assurez-vous de sauvegarder toutes les informations importantes avant de procéder.
                </p>
              </div>
            </div>
          </div>

          <div className="p-8 bg-white rounded-2xl shadow-xl md:p-12">
            
            {/* What Will Be Deleted */}
            <div className="mb-12">
              <h2 className="flex items-center mb-6 text-2xl font-bold text-gray-900">
                <Database className="mr-3 w-6 h-6 text-blue-500" />
                Données qui seront supprimées
              </h2>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="p-6 bg-red-50 rounded-xl border border-red-200">
                  <h3 className="mb-3 font-semibold text-red-900">Informations personnelles</h3>
                  <ul className="space-y-2 text-sm text-red-800">
                    <li>• Profil utilisateur complet</li>
                    <li>• Adresse e-mail et téléphone</li>
                    <li>• Informations de connexion</li>
                    <li>• Préférences et paramètres</li>
                  </ul>
                </div>
                <div className="p-6 bg-orange-50 rounded-xl border border-orange-200">
                  <h3 className="mb-3 font-semibold text-orange-900">Données financières</h3>
                  <ul className="space-y-2 text-sm text-orange-800">
                    <li>• Historique des transactions</li>
                    <li>• Catégories de dépenses</li>
                    <li>• Budgets et objectifs</li>
                    <li>• Rapports et analyses</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Process Information */}
            <div className="mb-12">
              <h2 className="flex items-center mb-6 text-2xl font-bold text-gray-900">
                <Clock className="mr-3 w-6 h-6 text-indigo-500" />
                Processus de suppression
              </h2>
              <div className="space-y-4">
                <div className="flex items-start p-4 bg-indigo-50 rounded-lg">
                  <div className="flex flex-shrink-0 justify-center items-center mr-4 w-8 h-8 text-sm font-bold text-white bg-indigo-500 rounded-full">1</div>
                  <div>
                    <h4 className="mb-1 font-semibold text-indigo-900">Demande de suppression</h4>
                    <p className="text-sm text-indigo-800">Remplissez le formulaire ci-dessous avec votre e-mail et la raison de votre demande</p>
                  </div>
                </div>
                <div className="flex items-start p-4 bg-indigo-50 rounded-lg">
                  <div className="flex flex-shrink-0 justify-center items-center mr-4 w-8 h-8 text-sm font-bold text-white bg-indigo-500 rounded-full">2</div>
                  <div>
                    <h4 className="mb-1 font-semibold text-indigo-900">Vérification d'identité</h4>
                    <p className="text-sm text-indigo-800">Nous vous enverrons un e-mail de confirmation pour vérifier votre identité</p>
                  </div>
                </div>
                <div className="flex items-start p-4 bg-indigo-50 rounded-lg">
                  <div className="flex flex-shrink-0 justify-center items-center mr-4 w-8 h-8 text-sm font-bold text-white bg-indigo-500 rounded-full">3</div>
                  <div>
                    <h4 className="mb-1 font-semibold text-indigo-900">Suppression des données</h4>
                    <p className="text-sm text-indigo-800">Vos données seront supprimées dans un délai maximum de 30 jours</p>
                  </div>
                </div>
                <div className="flex items-start p-4 bg-indigo-50 rounded-lg">
                  <div className="flex flex-shrink-0 justify-center items-center mr-4 w-8 h-8 text-sm font-bold text-white bg-indigo-500 rounded-full">4</div>
                  <div>
                    <h4 className="mb-1 font-semibold text-indigo-900">Confirmation</h4>
                    <p className="text-sm text-indigo-800">Vous recevrez une confirmation de la suppression complète</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Legal Information */}
            <div className="mb-12">
              <h2 className="flex items-center mb-4 text-2xl font-bold text-gray-900">
                <Shield className="mr-3 w-6 h-6 text-green-500" />
                Conformité RGPD
              </h2>
              <div className="p-6 bg-green-50 rounded-xl border border-green-200">
                <p className="mb-4 text-green-800">
                  Cette procédure respecte vos droits fondamentaux selon le Règlement Général sur la Protection des Données (RGPD) :
                </p>
                <ul className="space-y-2 text-green-800">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Article 17 - Droit à l'effacement :</strong> Vous pouvez demander la suppression de vos données personnelles</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Article 20 - Droit à la portabilité :</strong> Vous pouvez récupérer vos données avant suppression</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Délai légal :</strong> Maximum 30 jours pour traiter votre demande</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Deletion Form */}
            {!deletionConfirmed ? (
              <div className="mb-12">
                <h2 className="mb-6 text-2xl font-bold text-gray-900">
                  Demande de suppression
                </h2>
                <form onSubmit={handleDeleteRequest} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-700">
                      Adresse e-mail de votre compte *
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="votre.email@exemple.com"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="reason" className="block mb-2 text-sm font-medium text-gray-700">
                      Raison de la suppression *
                    </label>
                    <select
                      id="reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      required
                      className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    >
                      <option value="">Sélectionnez une raison</option>
                      <option value="privacy">Préoccupations de confidentialité</option>
                      <option value="no-longer-needed">Service plus nécessaire</option>
                      <option value="unlawful-processing">Traitement illégal des données</option>
                      <option value="withdraw-consent">Retrait du consentement</option>
                      <option value="other">Autre raison</option>
                    </select>
                  </div>

                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="flex items-start">
                      <Info className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-yellow-800">
                        <p className="mb-1 font-medium">Important :</p>
                        <ul className="space-y-1">
                          <li>• Assurez-vous d'utiliser l'e-mail associé à votre compte Burok</li>
                          <li>• Vérifiez que vous avez sauvegardé toutes les données importantes</li>
                          <li>• Cette action est irréversible et prendra effet dans les 30 jours</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isDeleting || !email || !reason}
                    className={`w-full py-4 px-6 rounded-lg font-medium text-white transition-all duration-200 ${
                      isDeleting || !email || !reason
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 hover:shadow-lg hover:scale-105'
                    }`}
                  >
                    {isDeleting ? (
                      <div className="flex justify-center items-center">
                        <div className="mr-2 w-5 h-5 rounded-full border-b-2 border-white animate-spin"></div>
                        Traitement en cours...
                      </div>
                    ) : (
                      <div className="flex justify-center items-center">
                        <Trash2 className="mr-2 w-5 h-5" />
                        Demander la suppression de mes données
                      </div>
                    )}
                  </button>
                </form>
              </div>
            ) : (
              /* Confirmation Message */
              <div className="py-12 text-center">
                <div className="inline-flex justify-center items-center mb-6 w-16 h-16 bg-green-100 rounded-full">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="mb-4 text-2xl font-bold text-gray-900">
                  Demande reçue avec succès !
                </h2>
                <p className="mx-auto mb-6 max-w-md text-gray-600">
                  Nous avons bien reçu votre demande de suppression. Vous allez recevoir un e-mail de confirmation 
                  dans les prochaines minutes.
                </p>
                <div className="p-6 mx-auto max-w-md bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="mb-2 font-semibold text-blue-900">Prochaines étapes :</h3>
                  <ul className="space-y-2 text-sm text-left text-blue-800">
                    <li>• Vérifiez votre boîte e-mail (et spam)</li>
                    <li>• Cliquez sur le lien de confirmation</li>
                    <li>• Vos données seront supprimées sous 30 jours</li>
                    <li>• Vous recevrez une confirmation finale</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Alternative Options */}
            <div className="p-8 text-center bg-gray-50 rounded-xl">
              <h2 className="mb-4 text-2xl font-bold text-gray-900">
                Pas encore sûr ?
              </h2>
              <p className="mb-6 text-gray-600">
                Avant de supprimer définitivement votre compte, vous pouvez aussi :
              </p>
              <div className="space-y-3">
                <a
                  href="/privacy"
                  className="inline-flex items-center px-6 py-3 mr-3 font-medium text-white bg-gray-600 rounded-full transition-all duration-200 hover:bg-gray-700 hover:shadow-lg"
                >
                  <Shield className="mr-2 w-4 h-4" />
                  Consulter notre politique de confidentialité
                </a>
                <a
                  href="mailto:support@burok.app"
                  className="inline-flex items-center px-6 py-3 font-medium text-white bg-blue-600 rounded-full transition-all duration-200 hover:bg-blue-700 hover:shadow-lg"
                >
                  <Info className="mr-2 w-4 h-4" />
                  Contacter le support
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default DataDelete;
